import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';

/**
 * Keeping and publishing are two things (D2): what is KEPT on a campaign whose
 * job is open is not public until someone publishes it. These three calls are
 * the app's side of that, for the sidebar (scenes 6, 7 and 9).
 */
export interface WaitingChange {
  /** The screen the change was made on, for the grouping in the list. */
  screen: string;
  /** What a person calls the thing that changed. */
  thing: string;
  /** For a text: the public words and the kept words (5b item 9). */
  before?: string;
  after?: string;
}

export interface WaitingChanges {
  /** Do changes wait at all on this campaign? Only while its job is open. */
  waits: boolean;
  count: number;
  changes: WaitingChange[];
  /**
   * D33: the campaign runs on LinkedIn or Google, whose running ads a publish
   * does not update. The list of changes says so.
   */
  keeps_note?: boolean;
  last_change_by: string | null;
  last_change_at: string | null;
  published_at: string | null;
  /**
   * Only on a campaign that was published and is CLOSED now: what the ad
   * builder and the application form kept since. It is applied when the
   * campaign is re-opened (Steven, 5 Oct 2026).
   */
  closed?: { count: number; changes: WaitingChange[] } | null;
}

/** The sidebar line of a closed campaign, in Steven's words (5 Oct 2026). */
export const closedLabel = (count: number): string =>
  `${count} ${
    count === 1 ? 'change' : 'changes'
  } will go live when you re-open`;

/** The exact words of the description (5c) for a publish sympl could not do. */
export const COULD_NOT_PUBLISH =
  'Could not publish, nothing was changed. Try again.';

const call = async (
  vacancyId: number,
  path: string,
  method: 'GET' | 'POST',
  body?: Record<string, unknown>
): Promise<WaitingChanges> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';

  const response = await fetch(`${base}/v4/vacancies/${vacancyId}/${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) throw new Error(`${path} answered ${response.status}`);

  const answer = await response.json();
  return answer?.data as WaitingChanges;
};

export const getWaitingChanges = (vacancyId: number) =>
  call(vacancyId, 'waiting-changes', 'GET');

export const publishChanges = (vacancyId: number) =>
  call(vacancyId, 'publish-changes', 'POST');

export const discardChanges = (vacancyId: number) =>
  call(vacancyId, 'discard-changes', 'POST');

/**
 * "Discard changes" on a CLOSED campaign. It says so: were the campaign
 * re-opened by a colleague meanwhile, the server refuses rather than
 * discard what now waits on the open campaign (review of the slice).
 */
export const discardClosedChanges = (vacancyId: number) =>
  call(vacancyId, 'discard-changes', 'POST', { while_closed: true });

/** "14:02", for "last change by Anna, 14:02" and "Published at 14:30". */
export const hourAndMinute = (moment: string | null): string => {
  if (!moment) return '';
  const date = new Date(moment);
  if (Number.isNaN(date.getTime())) return '';

  return `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`;
};

/** The sidebar label of scene 6, in the description's exact form. */
export const waitingLabel = (waiting: WaitingChanges): string => {
  const noun = waiting.count === 1 ? 'change' : 'changes';
  const who = waiting.last_change_by
    ? ` · last change by ${waiting.last_change_by}, ${hourAndMinute(
        waiting.last_change_at
      )}`
    : '';

  return `${waiting.count} ${noun} not yet public${who}`;
};

/**
 * Sent by the sidebar when "Publish changes" has happened. The ad builder
 * loads its ads again then: a new ad has its number now.
 */
export const CHANGES_WERE_PUBLISHED = 'sympl:changes-were-published';

/** Sent by a screen when a save of its own has landed, so the label follows. */
export const WORK_WAS_KEPT = 'sympl:work-was-kept';
